import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Clock3, LogOut, Loader2, Phone, Trash2, RefreshCw, Car, CheckCircle2, ClipboardList, Inbox, FileText, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/RichTextEditor";
import { adminLogin, getBookings, getBookingStats, getEnquiries, updateBookingStatus, deleteBooking, getArticles, createArticle, updateArticle, deleteArticle, getComplaints, getLostProperty, getCorporateAccounts, approveCorporateAccount } from "@/lib/api";

const KEY_STORE = "cc_admin_key";
const statusColors = {
  new: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-sky-50 text-sky-700 border-sky-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const CATEGORIES = ["Airport Transfers", "Chauffeur", "Travel Tips", "Business", "News"];
const emptyArticle = { title: "", category: "Travel Tips", excerpt: "", image: "", read_time: "4 min read", body: "", published: true };

function ArticleEditor({ open, onClose, initial, adminKey, onSaved }) {
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    setForm(initial ? { ...emptyArticle, ...initial } : emptyArticle);
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.excerpt.trim()) {
      toast.error("Title and excerpt are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, category: form.category, excerpt: form.excerpt,
        image: form.image, read_time: form.read_time, body: form.body, published: form.published,
      };
      if (isEdit) await updateArticle(initial.id, payload, adminKey);
      else await createArticle(payload, adminKey);
      toast.success(isEdit ? "Article updated." : "Article published.");
      onSaved();
      onClose();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="article-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-slate-900">{isEdit ? "Edit article" : "New article"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">Title</Label>
            <Input data-testid="article-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-white border-slate-300 rounded-lg" placeholder="Article headline" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="article-category-select" className="bg-white border-slate-300 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900 rounded-lg">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">Read time</Label>
              <Input data-testid="article-readtime-input" value={form.read_time} onChange={(e) => set("read_time", e.target.value)} className="bg-white border-slate-300 rounded-lg" placeholder="4 min read" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">Cover image URL</Label>
            <Input data-testid="article-image-input" value={form.image} onChange={(e) => set("image", e.target.value)} className="bg-white border-slate-300 rounded-lg" placeholder="https://…" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">Excerpt</Label>
            <Textarea data-testid="article-excerpt-input" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="bg-white border-slate-300 rounded-lg resize-none" rows={2} placeholder="Short summary shown on the blog list" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">Content</Label>
            <RichTextEditor value={form.body} onChange={(v) => set("body", v)} />
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="article-published-switch" checked={form.published} onCheckedChange={(v) => set("published", v)} />
            <span className="text-sm text-slate-600">{form.published ? "Published (visible on the site)" : "Draft (hidden)"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-lg border-slate-300 text-slate-600">Cancel</Button>
          <Button data-testid="article-save-btn" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-lg font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Login({ onAuth }) {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(key);
      localStorage.setItem(KEY_STORE, key);
      onAuth(key);
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-5">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center h-11 w-11 rounded-lg bg-brand text-white"><Clock3 className="h-6 w-6" strokeWidth={1.75} /></span>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Clocktower Admin</h1>
            <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">Restricted access</p>
          </div>
        </div>
        <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-2 block">Admin Key</Label>
        <Input data-testid="admin-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} className="bg-white border-slate-300 rounded-lg h-12 text-slate-900" placeholder="Enter admin key" />
        <Button type="submit" data-testid="admin-login-btn" disabled={loading} className="mt-5 w-full bg-brand hover:bg-brand-dark text-white rounded-lg py-3 h-auto font-semibold">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
        </Button>
      </form>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
        <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-50 text-brand"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
      </div>
      <p className="font-heading text-3xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [lostProps, setLostProps] = useState([]);
  const [corporates, setCorporates] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const [b, s, e, a, c, lp, ca] = await Promise.all([getBookings(k), getBookingStats(k), getEnquiries(k), getArticles(true), getComplaints(k), getLostProperty(k), getCorporateAccounts(k)]);
      setBookings(b); setStats(s); setEnquiries(e); setArticles(a); setComplaints(c); setLostProps(lp); setCorporates(ca); setAuthed(true);
    } catch {
      localStorage.removeItem(KEY_STORE);
      setAuthed(false); setKey("");
      toast.error("Session expired. Please sign in.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (key) load(key); }, [key, load]);

  const onAuth = (k) => { setKey(k); };
  const logout = () => { localStorage.removeItem(KEY_STORE); setAuthed(false); setKey(""); };

  const changeStatus = async (id, status) => {
    try { await updateBookingStatus(id, status, key); toast.success("Status updated."); load(key); }
    catch { toast.error("Update failed."); }
  };
  const remove = async (id) => {
    try { await deleteBooking(id, key); toast.success("Booking deleted."); load(key); }
    catch { toast.error("Delete failed."); }
  };

  const openNewArticle = () => { setEditing(null); setEditorOpen(true); };
  const openEditArticle = (a) => { setEditing(a); setEditorOpen(true); };
  const approveCorp = async (id) => {
    try { await approveCorporateAccount(id, key); toast.success("Account approved · onboarding email sent."); load(key); }
    catch { toast.error("Approval failed."); }
  };
  const removeArticle = async (id) => {
    try { await deleteArticle(id, key); toast.success("Article deleted."); load(key); }
    catch { toast.error("Delete failed."); }
  };

  if (!authed) return <Login onAuth={onAuth} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-lg bg-brand text-white"><Clock3 className="h-5 w-5" strokeWidth={1.75} /></span>
            <div><h1 className="font-heading text-lg font-bold text-slate-900 leading-none">Clocktower Cars Gatwick</h1><p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">Admin Dashboard</p></div>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="admin-refresh-btn" variant="outline" onClick={() => load(key)} className="rounded-lg border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline" onClick={logout} className="rounded-lg border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-10">
          <StatCard label="Total Bookings" value={stats.total} icon={ClipboardList} />
          <StatCard label="New" value={stats.new} icon={Car} />
          <StatCard label="Confirmed" value={stats.confirmed} icon={Clock3} />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} />
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-white border border-slate-200 rounded-lg">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-md data-[state=active]:bg-brand data-[state=active]:text-white">Bookings</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-md data-[state=active]:bg-brand data-[state=active]:text-white">Enquiries</TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles" className="rounded-md data-[state=active]:bg-brand data-[state=active]:text-white">Articles</TabsTrigger>
            <TabsTrigger value="complaints" data-testid="tab-complaints" className="rounded-md data-[state=active]:bg-brand data-[state=active]:text-white">Complaints{complaints.length > 0 && <span className="ml-1.5 text-[10px] bg-red-500 text-white rounded-full px-1.5">{complaints.length}</span>}</TabsTrigger>
            <TabsTrigger value="lost" data-testid="tab-lost" className="rounded-md data-[state=active]:bg-brand data-[state=active]:text-white">Lost property{lostProps.length > 0 && <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5">{lostProps.length}</span>}</TabsTrigger>
            <TabsTrigger value="corporates" data-testid="tab-corporates" className="rounded-md data-[state=active]:bg-brand data-[state=active]:text-white">Corporate{corporates.length > 0 && <span className="ml-1.5 text-[10px] bg-brand text-white rounded-full px-1.5">{corporates.length}</span>}</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 hover:bg-transparent">
                    {["Customer", "Journey", "Date / Time", "Vehicle", "Status", ""].map((h) => (
                      <TableHead key={h} className="text-slate-500 uppercase text-xs tracking-wider font-semibold">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-12">No bookings yet.</TableCell></TableRow>
                  )}
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="border-slate-100 hover:bg-slate-50">
                      <TableCell>
                        <p className="text-slate-900 font-medium">{b.name}</p>
                        <a href={`tel:${b.phone}`} className="text-xs text-brand flex items-center gap-1"><Phone className="h-3 w-3" /> {b.phone}</a>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-xs"><span className="text-slate-400">From</span> {b.pickup}<br /><span className="text-slate-400">To</span> {b.dropoff}</TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{b.date}<br /><span className="text-slate-400">{b.time}</span></TableCell>
                      <TableCell className="text-sm text-slate-600">{b.vehicle_type}<br /><span className="text-slate-400">{b.passengers} pax</span></TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                          <SelectTrigger data-testid={`status-select-${b.id}`} className={`h-9 rounded-lg border w-32 ${statusColors[b.status] || ""}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-slate-200 text-slate-900 rounded-lg">
                            {["new", "confirmed", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button data-testid={`delete-booking-${b.id}`} variant="ghost" size="icon" onClick={() => remove(b.id)} className="text-slate-400 hover:text-red-500 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="enquiries" className="mt-6">
            {enquiries.length === 0 ? (
              <div className="border border-slate-200 rounded-xl bg-white py-16 text-center text-slate-400"><Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />No enquiries yet.</div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {enquiries.map((e) => (
                  <div key={e.id} data-testid={`enquiry-${e.id}`} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div><p className="text-slate-900 font-medium">{e.name}</p><p className="text-xs text-slate-400">{e.email || "—"} · {e.phone || "—"}</p></div>
                      <Badge className="bg-slate-100 text-slate-500 rounded-md">{new Date(e.created_at).toLocaleDateString()}</Badge>
                    </div>
                    <p className="text-slate-600 text-sm">{e.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="articles" className="mt-6">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-slate-500">{articles.length} article{articles.length !== 1 ? "s" : ""}</p>
              <Button data-testid="new-article-btn" onClick={openNewArticle} className="bg-brand hover:bg-brand-dark text-white rounded-lg font-semibold">
                <Plus className="h-4 w-4 mr-2" /> New article
              </Button>
            </div>
            {articles.length === 0 ? (
              <div className="border border-slate-200 rounded-xl bg-white py-16 text-center text-slate-400"><FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />No articles yet.</div>
            ) : (
              <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      {["Title", "Category", "Status", ""].map((h) => (
                        <TableHead key={h} className="text-slate-500 uppercase text-xs tracking-wider font-semibold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {articles.map((a) => (
                      <TableRow key={a.id} data-testid={`article-row-${a.id}`} className="border-slate-100 hover:bg-slate-50">
                        <TableCell>
                          <p className="text-slate-900 font-medium max-w-md truncate">{a.title}</p>
                          <p className="text-xs text-slate-400">/blog/{a.slug}</p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{a.category}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-md ${a.published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                            {a.published ? "Published" : "Draft"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-end">
                            <Button data-testid={`edit-article-${a.id}`} variant="ghost" size="icon" onClick={() => openEditArticle(a)} className="text-slate-400 hover:text-brand hover:bg-brand-50">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button data-testid={`delete-article-${a.id}`} variant="ghost" size="icon" onClick={() => removeArticle(a.id)} className="text-slate-400 hover:text-red-500 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="complaints" className="mt-6">
            {complaints.length === 0 ? (
              <div className="border border-slate-200 rounded-xl bg-white py-16 text-center text-slate-400"><Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />No complaints yet.</div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {complaints.map((c) => (
                  <div key={c.id} data-testid={`complaint-${c.id}`} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                    <div className="flex justify-between items-start mb-3 gap-3">
                      <div>
                        <p className="text-slate-900 font-semibold">{c.name}</p>
                        <p className="text-xs text-slate-400 break-all">{c.email}{c.phone && ` · ${c.phone}`}</p>
                      </div>
                      <Badge className="bg-red-50 text-red-700 border-red-200 rounded-md shrink-0">{c.category}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-3">
                      {c.booking_ref && <span>Ref: <span className="text-slate-700 font-medium">{c.booking_ref}</span></span>}
                      {c.incident_date && <span>Incident: <span className="text-slate-700">{c.incident_date}</span></span>}
                      <span>Submitted: <span className="text-slate-700">{new Date(c.created_at).toLocaleString()}</span></span>
                    </div>
                    <p className="text-slate-600 text-sm whitespace-pre-wrap leading-relaxed">{c.message}</p>
                    <div className="mt-4 flex gap-2 text-xs">
                      <a href={`mailto:${c.email}?subject=Re: Clocktower Cars complaint`} className="inline-flex items-center gap-1 text-brand font-semibold hover:underline">Reply by email</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-slate-600 font-semibold hover:underline">Call {c.phone}</a>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="lost" className="mt-6">
            {lostProps.length === 0 ? (
              <div className="border border-slate-200 rounded-xl bg-white py-16 text-center text-slate-400"><Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />No lost-property reports yet.</div>
            ) : (
              <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      {["Reporter", "Journey", "Item", "Vehicle / notes", "Received"].map((h) => (
                        <TableHead key={h} className="text-slate-500 uppercase text-xs tracking-wider font-semibold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lostProps.map((lp) => (
                      <TableRow key={lp.id} data-testid={`lost-${lp.id}`} className="border-slate-100 hover:bg-slate-50 align-top">
                        <TableCell>
                          <p className="text-slate-900 font-medium">{lp.name}</p>
                          <p className="text-xs text-slate-400 break-all">{lp.email}</p>
                          <a href={`tel:${lp.phone}`} className="text-xs text-brand flex items-center gap-1 mt-1"><Phone className="h-3 w-3" /> {lp.phone}</a>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs">
                          <p className="text-slate-500 text-xs uppercase tracking-wide font-semibold">{lp.journey_date}</p>
                          {lp.pickup && <p><span className="text-slate-400">From</span> {lp.pickup}</p>}
                          {lp.dropoff && <p><span className="text-slate-400">To</span> {lp.dropoff}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700 max-w-sm whitespace-pre-wrap">{lp.item_description}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs">
                          {lp.vehicle_desc && <p className="mb-1">{lp.vehicle_desc}</p>}
                          {lp.additional_info && <p className="text-xs text-slate-500 whitespace-pre-wrap">{lp.additional_info}</p>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(lp.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="corporates" className="mt-6">
            {corporates.length === 0 ? (
              <div className="border border-slate-200 rounded-xl bg-white py-16 text-center text-slate-400"><Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />No corporate applications yet.</div>
            ) : (
              <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      {["Company", "Contact", "Volume", "Billing", "Notes", "Status", "Received"].map((h) => (
                        <TableHead key={h} className="text-slate-500 uppercase text-xs tracking-wider font-semibold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {corporates.map((ca) => (
                      <TableRow key={ca.id} data-testid={`corp-${ca.id}`} className="border-slate-100 hover:bg-slate-50 align-top">
                        <TableCell>
                          <p className="text-slate-900 font-semibold">{ca.company}</p>
                          {ca.vat && <p className="text-xs text-slate-400 mt-0.5">VAT {ca.vat}</p>}
                          {ca.departments && <p className="text-xs text-slate-500 mt-1">{ca.departments}</p>}
                        </TableCell>
                        <TableCell>
                          <p className="text-slate-900 font-medium">{ca.contact_name}</p>
                          {ca.position && <p className="text-xs text-slate-500">{ca.position}</p>}
                          <p className="text-xs text-slate-400 break-all mt-1">{ca.email}</p>
                          {ca.phone && <a href={`tel:${ca.phone}`} className="text-xs text-brand flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {ca.phone}</a>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{ca.monthly_journeys || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-xs whitespace-pre-wrap">{ca.billing_address || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-sm whitespace-pre-wrap">{ca.notes || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {ca.status === "approved" ? (
                            <span data-testid={`corp-status-${ca.id}`} className="inline-flex items-center gap-1 bg-brand/10 text-brand text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">Approved</span>
                          ) : (
                            <button
                              type="button"
                              data-testid={`corp-approve-${ca.id}`}
                              onClick={() => approveCorp(ca.id)}
                              className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-light text-white text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                            >
                              Approve · send welcome
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(ca.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <ArticleEditor open={editorOpen} onClose={() => setEditorOpen(false)} initial={editing} adminKey={key} onSaved={() => load(key)} />
    </div>
  );
}
